import React from 'react';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';
import { Link } from 'react-router-dom';
import { 
  Users, 
  Award, 
  Target, 
  Heart, 
  Shield, 
  Zap, 
  Globe, 
  Building2,
  Calendar,
  TrendingUp,
  Star,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  MapPin,
  Phone,
  Mail,
  Linkedin,
  Twitter,
  Facebook
} from 'lucide-react';

const About: React.FC = () => {
  const teamScrollRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  const checkScrollButtons = () => {
    if (teamScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = teamScrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  React.useEffect(() => {
    checkScrollButtons();
    window.addEventListener('resize', checkScrollButtons);
    return () => window.removeEventListener('resize', checkScrollButtons);
  }, []);

  const scrollTeam = (direction: 'left' | 'right') => {
    if (teamScrollRef.current) {
      const scrollAmount = 300; // Adjust scroll distance
      const currentScroll = teamScrollRef.current.scrollLeft;
      const newScroll = direction === 'left' 
        ? currentScroll - scrollAmount 
        : currentScroll + scrollAmount;
      
      teamScrollRef.current.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });
      
      // Check scroll buttons after scrolling
      setTimeout(checkScrollButtons, 300);
    }
  };

  const values = [
    {
      icon: Target,
      title: "Excellence",
      description: "We strive for excellence in every project, delivering solutions that exceed expectations and drive measurable results.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: Heart,
      title: "Customer Focus",
      description: "Our customers are at the heart of everything we do. We build lasting partnerships based on trust and mutual success.",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: Shield,
      title: "Integrity",
      description: "We conduct business with the highest ethical standards, ensuring transparency and reliability in all our relationships.",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: Zap,
      title: "Innovation",
      description: "We embrace cutting-edge technologies and innovative approaches to solve complex business challenges.",
      color: "from-orange-500 to-red-500"
    }
  ];

  const team = [
    {
      name: "Uma Mahesh",
      role: "CEO & Founder",
      image: "/images/team/image.jpg",
      bio: "15+ years of experience in Microsoft technologies and business transformation.",
      linkedin: "https://www.linkedin.com/in/uma-mahesh-d-8a3b8b76/",
      twitter: "https://x.com/crmonce"
    },
    {
      name: "Sri Krishna",
      role: "CTO",
      image: "",
      bio: "Expert in Dynamics 365 architecture and enterprise solutions.",
      linkedin: "#",
      twitter: "#"
    },
    {
      name: "Uttej Annam",
      role: "Human Resource",
      image: "",
      bio: "Human Resource Management Specialist.",
      linkedin: "#",
      twitter: "#"
    },
    {
      name: "Sumanth Annam",
      role: "Technical Consultant",
      image: "/images/team/sumanth.jpg",
      bio: "Technical Consultant specializing in Microsoft Dynamics 365 CRM.",
      linkedin: "https://www.linkedin.com/in/annam-sumanth-72b199113?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app",
      twitter: "#"
    },
    {
      name: "VamsiKrishna Jana",
      role: "Technical Consultant",
      image: "",
      bio: "Power Platform Solution Developer.",
      linkedin: "https://www.linkedin.com/in/jana-vamsikrishna-33ab08273/",
      twitter: "#"
    },
    {
      name: "Vianykumar Kudumaula",
      role: "Technical Consultant",
      image: "",
      bio: "Dynamics 365 Plugins Specialist.",
      linkedin: "www.linkedin.com/in/vinaykumarkudumula",
      twitter: "#"
    },
    {
      name: "Meera Reddy",
      role: "Business Analyst",
      image: "",
      bio: "Bridges the gap between business requirements and technical solutions.",
      linkedin: "#",
      twitter: "#"
    },
    {
      name: "Arjun Malhotra",
      role: "DevOps Engineer",
      image: "",
      bio: "Ensures smooth deployment and maintenance of Dynamics 365 solutions.",
      linkedin: "#",
      twitter: "#"
    }
  ];

  const achievements = [
    {
      year: "2024",
      title: "100+ Happy Clients",
      description: "Successfully served over 100 organizations across various industries."
    },
    {
      year: "2023",
      title: "Microsoft Partner",
      description: "Achieved Microsoft Partner status for Dynamics 365 and Power Platform."
    },
    {
      year: "2022",
      title: "Global Expansion",
      description: "Expanded our operations across the world, strengthening our global presence.."
    },
    {
      year: "2021",
      title: "Industry Recognition",
      description: "Recognized as a top Dynamics 365 implementation partner."
    },
    {
      year: "2020",
      title: "Company Founded",
      description: "CRMONCE was founded with a vision to transform businesses through technology."
    }
  ];

  const stats = [
    { label: "Years Experience", value: 15, suffix: "+" },
    { label: "Projects Completed", value: 27, suffix: "+" },
    { label: "Happy Clients", value: 33, suffix: "+" },
    { label: "Team Members", value: 35, suffix: "+" },
    { label: "Countries Served", value: 15, suffix: "+" },
    { label: "Success Rate", value: 89, suffix: "%" }
  ];

  return (
    <div className="min-h-screen">
      <SEOHead 
        title="About Us - CRMONCE Microsoft Dynamics 365 Experts"
        description="Learn about CRMONCE, a leading Microsoft Dynamics 365 and Power Platform consulting company. Meet our expert team and discover our mission to transform businesses through technology."
        keywords="About CRMONCE, Microsoft Dynamics 365 experts, Power Platform consultants, CRM consulting company, Microsoft partners"
        canonical="/about"
      />

      {/* Hero Section */}
      <section className="hero-gradient particle-bg relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container-custom relative z-10 py-12">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-bold text-white mb-6">
               <span className="gradient-text">About CRMONCE</span>
            </h1>
            <p className="text-base md:text-lg lg:text-xl xl:text-2xl text-black/90 max-w-3xl mx-auto mb-8">
              We are a team of passionate technology experts dedicated to transforming businesses through innovative Microsoft Dynamics 365 and Power Platform solutions.
            </p>
          </motion.div>
        </div>
      </section>

             {/* Company Story */}
       <section className="py-12 bg-white/95 backdrop-blur-xl">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                 <span className="gradient-text">Our Story</span>
              </h2>
              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                Founded in 2020, CRMONCE emerged from a simple yet powerful vision: to help businesses leverage the full potential of Microsoft Dynamics 365 and Power Platform to drive growth and efficiency.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                What started as a small team of passionate developers has grown into a global organization serving hundreds of clients across multiple industries. Our journey has been marked by continuous learning, innovation, and an unwavering commitment to customer success.
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                {stats.slice(0, 4).map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    className="text-center"
                  >
                    <div className="text-3xl font-bold text-primary-600 mb-2">
                      {stat.value}{stat.suffix}
                    </div>
                    <div className="text-gray-600 font-medium">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="floating-animation">
                <div className="bg-gradient-to-br from-primary-500 to-accent-500 rounded-3xl p-8 shadow-2xl">
                  <div className="text-center text-white">
                    <Building2 className="w-16 h-16 mx-auto mb-6" />
                    <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
                    <p className="text-lg leading-relaxed">
                      To empower organizations with cutting-edge technology solutions that drive innovation, efficiency, and sustainable growth.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

             {/* Values Section */}
       <section className="section-gradient py-12">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
             <span className="gradient-text">Our Values</span>
            </h2>
            <p className="text-xl text-black/90 max-w-3xl mx-auto">
              These core values guide everything we do and shape our relationships with clients, partners, and each other.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                className="feature-card text-center"
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${value.color} rounded-2xl flex items-center justify-center mb-6 mx-auto shadow-2xl`}>
                  <value.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

                           {/* Team Section */}
        <section className="py-12 bg-white/95 backdrop-blur-xl hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <span className="gradient-text"> Meet Our Team</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our leadership team brings together decades of experience in Microsoft technologies and business transformation.
            </p>
          </motion.div>

          <div className="relative">
                         {/* Left Arrow */}
             {canScrollLeft && (
               <button
                 onClick={() => scrollTeam('left')}
                 className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full shadow-lg border border-gray-200 flex items-center justify-center hover:bg-white hover:shadow-xl transition-all duration-300 group"
               >
                 <ArrowLeft className="w-6 h-6 text-gray-600 group-hover:text-primary-600 transition-colors" />
               </button>
             )}

             {/* Right Arrow */}
             {canScrollRight && (
               <button
                 onClick={() => scrollTeam('right')}
                 className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 z-10 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full shadow-lg border border-gray-200 flex items-center justify-center hover:bg-white hover:shadow-xl transition-all duration-300 group"
               >
                 <ArrowRight className="w-6 h-6 text-gray-600 group-hover:text-primary-600 transition-colors" />
               </button>
             )}

                         {/* Team Members Container */}
             <div 
               ref={teamScrollRef}
               className="flex overflow-x-auto gap-4 pb-6 scrollbar-hide px-4"
               onScroll={checkScrollButtons}
             >
              {team.map((member, index) => (
                                 <motion.div
                   key={member.name}
                   initial={{ opacity: 0, y: 50 }}
                   whileInView={{ opacity: 1, y: 0 }}
                   transition={{ delay: index * 0.1, duration: 0.8 }}
                                       className="feature-card text-center w-[160px] flex-shrink-0 p-2 h-[380px] flex flex-col"
                 >
                                       <div className="flex-1 flex flex-col">
                                             <div className="relative mb-6 h-[8.5rem]">
                          {member.image ? (
                            <img 
                              src={member.image} 
                              alt={member.name}
                              className="w-32 h-32 rounded-full mx-auto object-cover shadow-xl border-3 border-white"
                              style={{ objectPosition: 'center 20%' }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const fallback = target.nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className={`w-32 h-32 rounded-full mx-auto shadow-xl border-3 border-white flex items-center justify-center bg-gradient-to-br from-primary-500 to-accent-500 ${member.image ? 'hidden' : ''}`}
                            style={{ display: member.image ? 'none' : 'flex' }}
                          >
                            <span className="text-4xl font-bold text-white">
                              {member.name ? member.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'TM'}
                            </span>
                          </div>
                          <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-5 h-5 text-white" />
                          </div>
                        </div>
                                            <h3 className="text-lg font-bold text-gray-900 mb-2 h-[1.5rem] overflow-hidden">{member.name}</h3>
                       <p className="text-primary-600 font-semibold mb-3 text-sm h-[1.25rem] overflow-hidden">{member.role}</p>
                                              <div className="text-gray-600 text-xs leading-relaxed mb-4 h-[3.5rem] overflow-hidden bio-text">
                         <p className="line-clamp-3">{member.bio}</p>
                       </div>
                    </div>
                    <div className="flex justify-center space-x-3 mt-auto">
                     <a href={member.linkedin} className="text-gray-400 hover:text-primary-600 transition-colors">
                       <Linkedin className="w-5 h-5" />
                     </a>
                     <a href={member.twitter} className="text-gray-400 hover:text-primary-600 transition-colors">
                       <Twitter className="w-5 h-5" />
                     </a>
                   </div>
                 </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

             {/* Timeline Section */}
       <section className="section-gradient py-12">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
               <span className="gradient-text">Our Journey</span>
            </h2>
            <p className="text-xl text-black/90 max-w-3xl mx-auto">
              Key milestones that have shaped our growth and success over the years.
            </p>
          </motion.div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-px h-full w-0.5 bg-white/20"></div>
            <div className="space-y-12">
              {achievements.map((achievement, index) => (
                <motion.div
                  key={achievement.year}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.8 }}
                  className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  <div className="w-1/2 px-8">
                    <div className="feature-card">
                      <div className="text-2xl font-bold text-primary-600 mb-2">{achievement.year}</div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{achievement.title}</h3>
                      <p className="text-gray-600">{achievement.description}</p>
                    </div>
                  </div>
                  <div className="w-4 h-4 bg-white rounded-full border-4 border-primary-500 relative z-10"></div>
                  <div className="w-1/2 px-8"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

             {/* Stats Section */}
       <section className="py-12 bg-white/95 backdrop-blur-xl">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
               <span className="gradient-text">By The Numbers</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our impact and reach across the global business landscape.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                className="text-center"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-2xl">
                  <TrendingUp className="w-10 h-10 text-white" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {stat.value}{stat.suffix}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

             {/* CTA Section */}
       <section className="py-12 bg-gradient-to-r from-primary-600 to-accent-600">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Work With Us?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Join hundreds of organizations that trust CRMONCE to transform their business operations and drive growth.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-accent text-lg px-8 py-4"
              >
                Get Started Today
                <ArrowRight className="ml-2 w-5 h-5" />
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-outline text-lg px-8 py-4"
              >
                View Our Work
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
      
             {/* Back to Contact Section */}
       <section className="py-12 bg-gray-50">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              <span className="gradient-text">Ready to Get in Touch ?</span>
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Now that you know more about us, let's discuss how CRMONCE can help transform your business with Microsoft Dynamics 365 and Power Platform solutions.
            </p>
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-primary text-lg px-8 py-4"
              >
                Contact Our Team
                <ArrowRight className="ml-2 w-5 h-5" />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About; 